/**
 * Jobs Service
 * Handles fetching and managing job data
 */

import { httpClient } from '../lib/httpClient';
import axios from 'axios';
import { useAuthStore } from '../stores/auth.store';

import type {
  JobsResponse,
  JobsQueryParams,
  JobDetail,
  JobDetailQueryParams,
  GeneratePresignedUrlRequest,
  GeneratePresignedUrlResponse,
  DownloadJobPackageResponse,
  JobDownloadStatus,
  QuestionError,
  JobsExportQueryParams,
  JobsExportResponse
} from '../interfaces/apiInterfaces/jobs';
import type { JobType } from '../interfaces/apiInterfaces/enums/job.enums';

/**
 * Fetch jobs list with optional filters
 * @param params - Query parameters for filtering, pagination
 * @returns Jobs list with pagination info
 */
export async function fetchJobs(params?: JobsQueryParams): Promise<JobsResponse> {
  try {
    const response = await httpClient.get<JobsResponse>(
      '/v1/jobs',
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching jobs:', error);
    throw error;
  }
}

/**
 * Fetch detailed job information by ID
 * @param jobId - GUID referencing an existing job
 * @param params - Optional query parameters (e.g., question_status)
 * @returns Detailed job information
 */
export async function fetchJobById(jobId: string, params?: JobDetailQueryParams): Promise<JobDetail> {
  try {
    const response = await httpClient.get<JobDetail>(
      `/v1/jobs/${jobId}`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching job details for ID ${jobId}:`, error);
    throw error;
  }
}

/**
 * Request the creation of a compressed ZIP package for job questions.
 * This invokes the backend's questions_download internal service.
 * 
 * @param jobId - GUID referencing an existing job
 * @param status - One of: successed, failed, all, pending-graphics or published
 * @returns Presigned URL and S3 key for the ZIP package
 */
export async function downloadJobQuestionsPackage(
  jobId: string,
  status: JobDownloadStatus
): Promise<DownloadJobPackageResponse> {
  try {
    const response = await httpClient.post<DownloadJobPackageResponse>(
      `/v1/jobs/${jobId}/download-package`,
      {}, // Empty body
      {
        params: { 'question-status': status }
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Error requesting download package for job ${jobId}:`, error);
    throw error;
  }
}

/**
 * Generic logic for downloading questions.
 * This can be used as a centralized service to be called from other parts of the app
 * if they need to trigger a question download flow.
 */
export const questionsDownloadService = {
  downloadPackage: downloadJobQuestionsPackage,
  // Add other download-related methods here if needed in the future
};

/**
 * Generate presigned URL for job file upload
 * @param originalFilename - Original filename of the file to upload
 * @param jobType - Job type: 'create' or 'update'
 * @param jobId - Optional job ID (UUID) for adding files to existing job
 * @returns Presigned URL and job metadata
 */
export async function generatePresignedUrl(originalFilename: string, jobType: JobType, jobId?: string): Promise<GeneratePresignedUrlResponse> {
  try {
    debugger;
    // Get authenticated user ID from auth store
    const userId = useAuthStore.getState().user?.id;
    
    if (!userId) {
      throw new Error('User not authenticated. Please log in to upload files.');
    }

    const requestBody: GeneratePresignedUrlRequest = {
      user_id: userId,
      original_filename: originalFilename,
      job_type: jobType,
      ...(jobId && { job_id: jobId }),
    };

    const response = await httpClient.post<GeneratePresignedUrlResponse>(
      '/v1/jobs/generate-presigned-url',
      requestBody
    );
    return response.data;
  } catch (error) {
    console.error('Error generating presigned URL:', error);
    throw error;
  }
}

/**
 * Upload file to S3 using presigned URL
 * @param presignedUrl - The presigned URL from generatePresignedUrl
 * @param file - The file to upload
 * @param originalFilename - Original filename from API response
 * @param userId - User ID from API response
 * @param jobType - Job type ('create' or 'update')
 * @param onProgress - Optional progress callback (0-100)
 * @param signal - Optional AbortSignal for cancellation
 * @returns Upload response
 */
export async function uploadFileToS3(
  presignedUrl: string,
  file: File,
  originalFilename: string,
  userId: string,
  jobType: JobType,
  onProgress?: (progress: number) => void,
  signal?: AbortSignal
): Promise<void> {
  try {
    // Create a new axios instance without interceptors for S3 upload
    const s3Client = axios.create();
    
    await s3Client.put(presignedUrl, file, {
      headers: {
        'Content-Type': 'application/zip',
        // Encode filename to handle Arabic/Unicode characters in HTTP headers
        'x-amz-meta-original-filename': originalFilename,
        'x-amz-meta-user-id': userId,
        'x-amz-meta-job-type': jobType,
      },
      transformRequest: [(data) => data], // Prevent axios from transforming the data
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
      signal,
    });
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Upload cancelled by user');
    } else {
      console.error('Error uploading file to S3:', error);
    }
    throw error;
  }
}

/**
 * Delete a specific question from a job
 * @param jobId - GUID referencing an existing job
 * @param questionId - The unique identifier of the question to delete
 * @returns Promise that resolves when the question is deleted (204 No Content)
 */
export async function deleteJobQuestion(jobId: string, questionId: string): Promise<void> {
  try {
    await httpClient.delete<void>(
      `/v1/jobs/${jobId}/questions/${questionId}`
    );
  } catch (error) {
    console.error(`Error deleting question ${questionId} from job ${jobId}:`, error);
    throw error;
  }
}

/**
 * Fetch all questions with errors for a specific job
 * @param jobId - GUID referencing an existing job
 * @returns Array of questions with errors (empty array if none)
 */
export async function fetchJobQuestionErrors(jobId: string): Promise<QuestionError[]> {
  try {
    const response = await httpClient.get<QuestionError[]>(
      `/v1/jobs/${jobId}/log-errors`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching question errors for job ${jobId}:`, error);
    throw error;
  }
}

/**
 * Fetch question errors for a specific file in a job
 * @param jobId - GUID referencing an existing job
 * @param fileId - GUID referencing a specific file in the job
 * @returns Array of question errors from the specified file
 */
export async function fetchFileQuestionErrors(jobId: string, fileId: string): Promise<QuestionError[]> {
  try {
    const response = await httpClient.get<QuestionError[]>(
      `/v1/jobs/${jobId}/files/${fileId}/log-errors`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching question errors for file ${fileId} in job ${jobId}:`, error);
    throw error;
  }
}

/**
 * Export jobs to CSV via email queue
 * @param params - Query parameters for filtering (without pagination)
 * @returns Export response indicating the request was queued
 */
export async function exportJobsToCSV(params?: JobsExportQueryParams): Promise<JobsExportResponse> {
  try {
    const response = await httpClient.post<JobsExportResponse>(
      '/v1/jobs/export/email',
      {},
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Error exporting jobs to CSV:', error);
    throw error;
  }
}
